import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import FiskLogo from "@/components/FiskLogo";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(!!token);
  const [tokenError, setTokenError] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const submittingRef = useRef(false);

  // Verifica o token uma vez ao carregar (sem consumir)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/reset-password/verify", { token });
        if (cancelled) return;
        setMaskedEmail(data.email || "");
      } catch (e) {
        if (cancelled) return;
        setTokenError(formatApiError(e) || "Link inválido");
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || loading) return; // guard síncrono contra double-submit
    if (pwd.length < 6) return toast.error("A senha deve ter no mínimo 6 caracteres");
    if (pwd !== confirm) return toast.error("As senhas não conferem");
    submittingRef.current = true;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pwd });
      toast.success("Senha redefinida! Você já pode entrar.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
      submittingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="h-16 bg-black flex items-center px-4 sm:px-8">
        <FiskLogo to="/login" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-6" data-testid="reset-password-page">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-[#E30613] font-medium"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-3 h-3" /> Voltar ao login
          </Link>

          {!token || tokenError ? (
            <div className="border border-red-200 bg-red-50 rounded-sm p-6" data-testid="reset-no-token">
              <AlertCircle className="w-10 h-10 text-red-600 mb-3" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {tokenError || "Link inválido"}
              </h2>
              <p className="text-sm text-neutral-700 mt-3">
                {token
                  ? "Este link já foi utilizado ou expirou. Solicite um novo em \"Esqueci minha senha\"."
                  : "O endereço acessado não contém um token de redefinição. Solicite um novo link em \"Esqueci minha senha\"."}
              </p>
              <Link to="/forgot-password">
                <Button className="mt-4 bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="request-new-reset-link">
                  Pedir novo link
                </Button>
              </Link>
            </div>
          ) : verifying ? (
            <div className="border border-neutral-200 bg-neutral-50 rounded-sm p-6" data-testid="reset-verifying">
              <ShieldCheck className="w-10 h-10 text-[#E30613] mb-3 animate-pulse" />
              <p className="text-sm text-neutral-700">Validando link...</p>
            </div>
          ) : (
            <>
              <div>
                <div className="inline-flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-5 h-5 text-[#E30613]" />
                  <span className="text-xs uppercase tracking-[0.3em] text-[#E30613] font-semibold">Redefinir senha</span>
                </div>
                <h2 className="text-3xl font-bold text-neutral-900">Crie uma nova senha</h2>
                <p className="text-sm text-neutral-500 mt-2">
                  {maskedEmail ? (
                    <>Definindo nova senha para <strong className="text-neutral-800">{maskedEmail}</strong>. </>
                  ) : null}
                  Use pelo menos 6 caracteres. Depois de salvar, entre no portal com a nova senha.
                </p>
              </div>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nova senha</Label>
                  <Input
                    id="new-password"
                    data-testid="reset-new-password-input"
                    type="password"
                    required
                    minLength={6}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="rounded-sm"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar senha</Label>
                  <Input
                    id="confirm-password"
                    data-testid="reset-confirm-password-input"
                    type="password"
                    required
                    minLength={6}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repita a nova senha"
                    className="rounded-sm"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                  className="w-full bg-[#E30613] hover:bg-[#B3040E] text-white rounded-sm h-11 font-semibold"
                >
                  {loading ? "Salvando..." : "Redefinir senha"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
